import type { ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { useBoolean } from 'usehooks-ts';

import { TaskAttribute } from '../task-attribute';

export type TaskEnumProps<T extends string | number = string> = {
  label: string;
  placeholder: string;
  value: T;
  options: T[];
  renderOption?: (option: T) => ReactNode;
  onChange?: (newValue: T) => Promise<void>;
};

export function TaskEnum<T extends string | number>({
  label,
  placeholder,
  value,
  options,
  renderOption = (n) => n,
  onChange,
}: TaskEnumProps<T>) {
  const loading = useBoolean(false);

  async function onSave(value: T) {
    loading.setTrue();
    try {
      await onChange?.(value);
    } finally {
      loading.setFalse();
    }
  }

  return (
    <TaskAttribute label={label}>
      <Select
        onValueChange={(val) => {
          const selected = options.find((o) => o.toString() === val);
          if (selected !== undefined) onSave(selected as T);
        }}
        value={value?.toString()}
        disabled={loading.value || !onChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {options.map((v) => (
              <SelectItem key={v} value={v.toString()}>
                {renderOption(v)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </TaskAttribute>
  );
}
